import { type FileInfo } from '../../../utils/types'

/**
 * Ensures a URL ends with a trailing slash
 */
function ensureTrailingSlash(url: string): string {
  return url.endsWith('/') ? url : `${url}/`
}

/**
 * Extracts the self URL from a value that can be either a string or an object with a self property
 */
function extractSelfUrl(value: unknown): string | undefined {
  if (typeof value === 'string' && value) return value
  if (typeof value === 'object' && value !== null && 'self' in value) {
    const self = (value as { self?: unknown }).self
    if (typeof self === 'string' && self) return self
  }
  return undefined
}

/**
 * Derives the files URL from formState.
 * 
 * Priority:
 * 1. If files is a string URL, use it directly
 * 2. If files is an array with items, extract URL from first file's self
 * 3. If self exists, construct self + 'files/'
 * 
 * @param formState - The form state object that may contain files and/or self
 * @returns The files URL or undefined if it cannot be determined
 */
export function deriveFilesUrl(formState: any): string | undefined {
  if (!formState) return undefined

  // Case 1: files is a string URL
  if (formState.files && typeof formState.files === 'string') {
    return ensureTrailingSlash(formState.files)
  }

  // Case 2: files is an array with FileInfo objects
  if (Array.isArray(formState.files) && formState.files.length > 0) {
    const firstFile = formState.files[0] as FileInfo
    if (firstFile && firstFile.self) {
      // Extract parent URL from file's self
      // Example: "v1/items/articles/123/files/456/" -> "v1/items/articles/123/files/"
      const trimmed = firstFile.self.endsWith('/') ? firstFile.self.slice(0, -1) : firstFile.self
      const lastSlash = trimmed.lastIndexOf('/')
      if (lastSlash !== -1) {
        // Get the path up to the last slash (this gives us the files list URL)
        return ensureTrailingSlash(trimmed.slice(0, lastSlash + 1))
      }
    }
  }

  // Case 3: Use self to construct files URL
  const selfUrl = extractSelfUrl(formState.self || formState)
  if (selfUrl) {
    return ensureTrailingSlash(`${ensureTrailingSlash(selfUrl)}files`)
  }

  return undefined
}

